/**
 * Analytics — the business view, where every number can be interrogated.
 *
 * This screen produces the figures a client makes decisions on, and **a number in a report is
 * believed**. The system this product replaces reported 1,095 re-introductions where the truth was
 * 217, because a query read a column as it stands today to answer a question about how things
 * stood when each message was sent. Nobody caught it for months. So three things are on the screen
 * beside every figure, not behind a link:
 *
 *  1. **Its window.** "p50" with no window is not a number.
 *  2. **Its tense** — measured from when each event happened, or the state right now. That is the
 *     `temporal` field, and it is the fix for the 1,095 bug made visible.
 *  3. **Its definition**, under a disclosure: the SQL that ran, verbatim, and who owns it.
 *
 * And a fourth thing, where there is no figure: **the sentence saying why**. A tile that cannot
 * compute says so. It never shows a zero, because "no cost rows in this window" and "it cost
 * nothing" are opposite facts and one of them ends up in a board pack.
 *
 * Nothing on this page is computed in the browser. Every number, including every bucket of every
 * breakdown, arrives from `GET /api/v1/analytics` with its own `metrics.definitions` row — because
 * the way "every number has a definition" actually gets broken is a page working one out locally
 * from two fields it already had.
 *
 * Author: Danish Khan
 */
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { ErrorState } from '../components/ErrorState.tsx';
import {
  analyticsApi, formatMetric, temporalLabel,
  type AnalyticsClient, type AnalyticsPanel, type MetricValue,
} from './analyticsApi.ts';

export interface AnalyticsProps {
  /** Injected in tests. The page is otherwise pointed at `/api/v1/analytics`. */
  client?: AnalyticsClient;
}

export function AnalyticsPage({ client = analyticsApi }: AnalyticsProps = {}): ReactNode {
  const q = useQuery({ queryKey: ['analytics'], queryFn: () => client.panels() });

  if (q.isError) {
    return <ErrorState error={q.error} context="load analytics" onRetry={() => void q.refetch()} />;
  }

  const lowerIsBetter = new Set(q.data?.lowerIsBetter ?? []);
  const panels = q.data?.panels ?? [];

  return (
    <div className="h1-scroll h-full overflow-y-auto px-5 py-5">
      <div className="mx-auto flex max-w-6xl flex-col gap-6">
        <header>
          <h1 className="text-base font-semibold text-fg">Analytics</h1>
          <p className="mt-1 max-w-3xl text-xs text-fg-muted">
            Every figure below carries the window it covers and the tense it is written in, and
            opens to show the query that produced it. A figure that cannot be computed says why
            instead of showing a zero.
          </p>
        </header>

        {q.isLoading ? (
          Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="h1-card h-40 animate-pulse bg-surface-2" />
          ))
        ) : panels.length === 0 ? (
          // Cannot happen against today's `ALL_PANELS`, which is not empty. Here because a header
          // with nothing under it is indistinguishable from a page that failed silently, and the
          // one thing this screen must never do is leave a reader to guess at a missing figure.
          <p className="text-sm text-fg-muted">
            No panels came back. Nothing is claimed about these numbers until they do.
          </p>
        ) : (
          panels.map((panel) => <Panel key={panel.id} panel={panel} lowerIsBetter={lowerIsBetter} />)
        )}
      </div>
    </div>
  );
}

function Panel({ panel, lowerIsBetter }: { panel: AnalyticsPanel; lowerIsBetter: Set<string> }): ReactNode {
  const series = panel.metrics.filter((m) => m.definition.shape === 'series');
  const scalars = panel.metrics.filter((m) => m.definition.shape === 'scalar');

  return (
    <section className="h1-card flex flex-col" data-testid={`panel-${panel.id}`}>
      <header className="border-b border-line px-4 py-3">
        <h2 className="text-sm font-semibold text-fg">{panel.title}</h2>
        {/*
          The panel's note is not decoration. It is where this page says what it is NOT claiming —
          that there is no return-on-investment tile because nothing in the database records what a
          won deal is worth, and that a spend total is withheld entirely when a single message in
          the window could not be priced. A reader who does not find a number they expected is
          otherwise left to assume they missed it.
        */}
        <p className="mt-1 max-w-3xl text-xs leading-relaxed text-fg-muted">{panel.note}</p>
      </header>

      {scalars.length > 0 && (
        <div className="flex flex-wrap gap-2.5 px-4 py-3.5">
          {scalars.map((m) => (
            <MetricCard key={m.name} metric={m} lowerIsBetter={lowerIsBetter.has(m.name)} />
          ))}
        </div>
      )}

      {series.map((m) => (
        <Series key={m.name} metric={m} />
      ))}
    </section>
  );
}

/**
 * One number, its window, its tense, and — when there is no number — the sentence saying why.
 *
 * Exported because Home renders the same card. Two tiles that show a figure differently are two
 * places to forget the definition, and Home's money and speed tiles can be unavailable for exactly
 * the reasons this card exists to print.
 */
export function MetricCard({ metric, lowerIsBetter = false }: { metric: MetricValue; lowerIsBetter?: boolean }): ReactNode {
  const { definition: d, value, delta, unavailable } = metric;
  const good = delta === null || delta === 0 ? null : lowerIsBetter ? delta < 0 : delta > 0;

  return (
    <div
      className="h1-card flex min-w-[13rem] flex-1 flex-col gap-1 px-3.5 py-3"
      data-testid={`metric-${d.name}`}
    >
      <span className="text-[11px] font-medium tracking-wide text-fg-muted uppercase">{d.label}</span>

      {unavailable !== null ? (
        <>
          <span className="text-lg font-semibold text-fg-subtle">Unavailable</span>
          {/*
            The reason, in full, on the tile. Not a tooltip: somebody reading a screenshot of this
            page has to be able to see why the number is missing, or they will fill the gap in
            themselves with a zero.
          */}
          <span className="text-[11px] leading-relaxed text-fg-muted" data-testid={`metric-unavailable-${d.name}`}>
            {unavailable}
          </span>
        </>
      ) : (
        <>
          <span className="text-2xl font-semibold tabular-nums text-fg">
            {formatMetric(value, d.unit)}
          </span>
          {delta !== null && delta !== 0 && (
            <span className={clsx('text-[11px] font-medium', good ? 'text-ok' : 'text-danger')}>
              {delta > 0 ? '▲' : '▼'} {formatMetric(Math.abs(delta), d.unit)} on the window before
            </span>
          )}
        </>
      )}

      <Definition metric={metric} />
    </div>
  );
}

/** A breakdown. Every bucket is a number under the parent's definition, window and tense. */
function Series({ metric }: { metric: MetricValue }): ReactNode {
  const { definition: d, rows, unavailable } = metric;
  const max = Math.max(1, ...(rows ?? []).map((r) => Math.abs(r.value)));

  return (
    <div className="border-t border-line px-4 py-3.5" data-testid={`metric-${d.name}`}>
      <p className="text-[11px] font-medium tracking-wide text-fg-muted uppercase">{d.label}</p>

      {rows === null ? (
        <p className="mt-1.5 text-xs text-fg-muted" data-testid={`metric-unavailable-${d.name}`}>
          <span className="font-semibold text-fg-subtle">Unavailable. </span>
          {unavailable}
        </p>
      ) : (
        <ul className="mt-2 flex flex-col gap-1.5">
          {rows.map((r) => (
            <li key={r.bucket} className="flex items-center gap-3">
              <span className="w-40 shrink-0 truncate text-xs text-fg">{r.label}</span>
              <span className="h-2 flex-1 overflow-hidden rounded-full bg-surface-2">
                <span
                  className="block h-full rounded-full bg-brand"
                  style={{ width: `${Math.round((Math.abs(r.value) / max) * 100)}%` }}
                />
              </span>
              <span className="w-24 shrink-0 text-right text-xs font-semibold tabular-nums text-fg">
                {formatMetric(r.value, d.unit)}
              </span>
            </li>
          ))}
        </ul>
      )}

      <Definition metric={metric} />
    </div>
  );
}

/**
 * The window, the tense, and the query — on the tile, and openable.
 *
 * `aria-describedby` points at a permanently rendered element rather than at the open disclosure:
 * an id that only exists while a panel is expanded is an attribute that dangles for most of the
 * element's life, which is the classic version of this bug and leaves a screen reader with a
 * number and no definition at all.
 */
function Definition({ metric }: { metric: MetricValue }): ReactNode {
  const d = metric.definition;
  return (
    <>
      <span className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-fg-subtle">
        <span>{d.window}</span>
        <span aria-hidden>·</span>
        <span>{temporalLabel(d.temporal)}</span>
      </span>

      <span id={`metric-def-${d.name}`} className="sr-only" data-testid={`metric-definition-${d.name}`}>
        {d.label}. Metric {d.name}, window {d.window}, {temporalLabel(d.temporal)}, owned by{' '}
        {d.owner}. Defined as: {d.sql}
      </span>

      <details className="mt-1.5 group">
        <summary className="cursor-pointer list-none text-[11px] font-medium text-brand-ink hover:underline">
          Definition
        </summary>
        <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]">
          <dt className="text-fg-subtle">metric</dt>
          <dd className="font-mono text-fg-muted">{d.name}</dd>
          <dt className="text-fg-subtle">window</dt>
          <dd className="text-fg-muted">{d.window}</dd>
          <dt className="text-fg-subtle">tense</dt>
          {/* The plain-English half only. `point_in_time` is the enum the SQL is tagged with; a
              reader who needs it has the query open right below this line. */}
          <dd className="text-fg-muted">{temporalLabel(d.temporal)}</dd>
          <dt className="text-fg-subtle">owner</dt>
          <dd className="text-fg-muted">{d.owner}</dd>
        </dl>
        <pre className="mt-1.5 max-h-48 overflow-auto rounded-md bg-surface-2 p-2 font-mono text-[10px] leading-relaxed whitespace-pre-wrap text-fg-muted">
          {d.sql}
        </pre>
      </details>
    </>
  );
}
